/* eslint-disable react/prop-types */
import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Link, formatPattern} from 'react-router';
import {List, Map} from 'immutable';
import {GuessModel} from 'resources/Guess/Guess.model';

import {ERROR_TYPES} from 'client/constants';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import PracticeView from 'client/components/PracticeView/PracticeView.react';

import {MobilePracticeViewProvider} from 'components/PracticeView/MobilePracticeViewProvider.context';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import QuestionActions, {
  loadGuess,
  clearGuess
} from 'components/PracticeView/QuestionActions/QuestionActions.react';
import {
  getPreviousQuestionState,
  getNextQuestionState
} from 'components/PracticeView/helpers/questionNavigation.utils';
import {getGuessesFromAssignment} from 'components/PracticeView/helpers/guessResultManager';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import HeadingActions from 'components/PracticeView/HeadingActions/HeadingActions.react';

import ViewingStudentSubmissionToolbar from './ViewStudentSubmissionToolbar.react';
import VSSQuestionList from './ViewStudentSubmissionQuestionList.react';

import {
  getStudentSubmissionQuestionSetsQuery,
  getAssignmentsQuery,
  getStudentAssignmentQuery
} from './ViewStudentSubmission.queries';

import {
  Assignment,
  QuestionSet,
  Question,
  QuestionList,
  Guess
} from './ViewStudentSubmission.types';

const getHeadingActions = () => {
  const actions = [<HeadingActions.QuestionFeedbackItem key='QuestionFeedbackItem' />];
  return <HeadingActions>{actions}</HeadingActions>;
};

const ViewStudentSubmission = ({router, params, location}) => {
  const {classroomId} = params;
  const [isLoading, setIsLoading] = useState(true);
  const [assignment, setAssignment] = useState<Assignment>({} as Assignment);
  const [activeQuestion, setActiveQuestion] = useState<Question>({} as Question);
  const [activeQuestionSet, setActiveQuestionSet] = useState<QuestionSet>({} as QuestionSet);
  const [paginatedResultsManager, setPaginatedResultsManager] = useState<PaginatedResultsManager>(
    {} as PaginatedResultsManager
  );
  const [questionSets, setQuestionSets] = useState<List<QuestionList>>(List());
  const [guessesMap, setGuessesMap] = useState<Map<string, Guess>>(Map());
  const {studentId, assignmentId, questionId} = params;

  const setActiveQuestionState = useCallback(
    (question, questionSet) => {
      setActiveQuestion(question);
      setActiveQuestionSet(questionSet);
      const newRoute = formatPattern(
        '/classes/:classroomId/assignments/:assignmentId/student-detail/:studentId/question/:questionId/view',
        {
          classroomId,
          assignmentId,
          studentId,
          questionId: questionSet?.getId()
        }
      );
      router.push({...location, pathname: newRoute});
    },
    [assignmentId, classroomId, location, router, studentId]
  );

  const executeQuery = useCallback(async () => {
    try {
      await getStudentSubmissionQuestionSetsQuery(
        assignmentId,
        questionId,
        classroomId
      ).getResourcePromise();
    } catch (e) {
      history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
    }

    const currentPaginatedResultsManager = new PaginatedResultsManager({
      initialQuery: getStudentSubmissionQuestionSetsQuery(assignmentId, questionId, classroomId),
      onUpdate: (incomingQuestionSets) => {
        if (incomingQuestionSets) {
          setQuestionSets((prevQuestionSets) => {
            const updatedQuestionSets = prevQuestionSets.concat(
              incomingQuestionSets
            ) as List<QuestionList>;
            const sortedUpdatedQuestionSets = updatedQuestionSets.sortBy((item) =>
              item!.getMeta().getAssignmentPosition()
            );
            return sortedUpdatedQuestionSets.toList();
          });
        }
      }
    });
    setPaginatedResultsManager(currentPaginatedResultsManager);

    let currentQuestionSet;
    let currentQuestion;
    if (questionId) {
      const filteredQuestionSets = currentPaginatedResultsManager
        .getResults()
        .filter((questionSet) => questionSet.getId() === questionId);
      currentQuestionSet = filteredQuestionSets.get(0);
      currentQuestion = currentQuestionSet?.getQuestions().first() || null;
    } else {
      currentQuestionSet = currentPaginatedResultsManager.getResults().first() || null;
      currentQuestion = currentQuestionSet?.getQuestions().first() || null;
      router.replace({
        ...location,
        pathname: `/classes/${classroomId}/assignments/${assignmentId}/student-detail/${studentId}/question/${currentQuestionSet.getId()}/view`
      });
    }

    const currentQuestionSets = currentPaginatedResultsManager.getResults();

    setActiveQuestionState(currentQuestion, currentQuestionSet);
    setQuestionSets(currentQuestionSets);
    setIsLoading(false);
  }, [assignmentId, classroomId, location, questionId, router, setActiveQuestionState, studentId]);

  useEffect(() => {
    if (isLoading) {
      executeQuery();
    }
  }, [isLoading, executeQuery]);

  useEffect(() => {
    const fetchGuesses = async () => {
      try {
        const query = getStudentAssignmentQuery({assignmentId, studentId});
        const response = await query.getResourcePromise();
        const guessesFromAssignment = getGuessesFromAssignment(response);
        setGuessesMap(guessesFromAssignment);
      } catch (e) {
        history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
      }
    };

    fetchGuesses();
  }, [studentId, assignmentId]);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const query = getAssignmentsQuery(assignmentId, studentId, classroomId);
        const fetchedAssignment = await query.getResourcePromise();
        setAssignment(fetchedAssignment);
      } catch (e) {
        history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
      }
    };
    fetchAssignments();
  });

  useEffect(() => {
    const activeGuess =
      guessesMap &&
      activeQuestion &&
      guessesMap.get(activeQuestion.getId && activeQuestion.getId())?.get('guess');
    // this loads the guess into the store used by XInputArea
    if (activeQuestion && activeGuess) {
      const questionStoreName = activeQuestion.getStore().getName();
      loadGuess(questionStoreName, activeGuess);
    }

    // if activeQuestion is an empty object (initial) and there isn't an activeGuess,
    // then clear the guess store so previous guesses dont appear
    if (Object.keys(activeQuestion).length !== 0 && !activeGuess) {
      const questionStoreName = activeQuestion.getStore().getName();
      clearGuess(questionStoreName);
    }
  }, [guessesMap, activeQuestion, studentId, assignmentId]);

  const activeGuess =
    (guessesMap &&
      activeQuestion &&
      guessesMap.get(activeQuestion.getId && activeQuestion.getId())?.get('guess')) ||
    new GuessModel({});

  // taken directly from SPV
  const makeNavigateBackProps = ({
    paginatedQuestionsListManager,
    activeQuestionSet: questionSet,
    activeQuestion: question
  }: any) => {
    const currentQuestionSets = paginatedQuestionsListManager.getResults();
    if (currentQuestionSets.isEmpty()) {
      return makeNavigateProps(null);
    }

    const previousSetAndQuestion = getPreviousQuestionState({
      questionSets: currentQuestionSets,
      activeSet: questionSet,
      activeQuestion: question
    });
    return makeNavigateProps(previousSetAndQuestion);
  };

  // taken directly from SPV
  const makeNavigateNextProps = ({
    paginatedQuestionsListManager,
    activeQuestionSet: questionSet,
    activeQuestion: question
  }: any) => {
    const currentQuestionSets = paginatedQuestionsListManager.getResults();
    if (currentQuestionSets.isEmpty()) {
      return makeNavigateProps(null);
    }
    const nextSetAndQuestion = getNextQuestionState({
      questionSets: currentQuestionSets,
      activeSet: questionSet,
      activeQuestion: question
    });
    return makeNavigateProps(nextSetAndQuestion);
  };

  // taken directly from SPV
  const makeNavigateProps = (upcomingSetAndQuestion) => {
    if (!upcomingSetAndQuestion) {
      return {disabled: true, handleNavigation: undefined};
    }
    const {questionSet, question} = upcomingSetAndQuestion;
    return {
      disabled: false,
      handleNavigation: () => {
        scrollToQuestionByIdFunc.current(questionSet.getId());
        setActiveQuestionState(question, questionSet);
      }
    };
  };

  const getFooterActions = () => {
    const {disabled, handleNavigation} = makeNavigateNextProps({
      paginatedQuestionsListManager: paginatedResultsManager,
      activeQuestionSet,
      activeQuestion
    });

    // Make a list of question actions that we can conditionally push more actions to
    const actions = [
      <QuestionActions.MoreInfo
        questionSetId={activeQuestionSet.getId()}
        userId={sessionStore.getUserId()}
      />,
      <QuestionActions.Next onClick={handleNavigation} disabled={disabled} />
    ];

    if (sessionStore.isSuper()) {
      actions.push(
        <QuestionActions.EditQuestionLink
          question={activeQuestion}
          questionSet={activeQuestionSet}
        />
      );
    }
    return (
      <QuestionActions questionStoreName={activeQuestion.getStore().getName()}>
        {actions}
      </QuestionActions>
    );
  };

  const activeSubject =
    activeQuestionSet && activeQuestionSet.getSubject && activeQuestionSet.getSubject();

  const scrollToQuestionByIdFunc = useRef((id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  });

  if (!questionSets.size) {
    return null;
  }

  return (
    <MobilePracticeViewProvider>
      <PracticeView
        activeQuestion={activeQuestion}
        activeQuestionSet={activeQuestionSet}
        activeSubject={activeSubject}
        activeQuestionStore={activeQuestion && activeQuestion.getStore()}
        activeGuess={activeGuess}
        questionSets={questionSets}
        isLoading={isLoading}
        titleBar={
          <TitleBar
            className='u-color_blue-500'
            title={assignment?.getName?.() || ''}
            backButton={
              <TitleBar.BackButton
                as={Link}
                label='Back To Assignments'
                to={location.state?.from || `/classes/${classroomId}/assignments`}
              />
            }
          />
        }
        toolbar={<ViewingStudentSubmissionToolbar assignment={assignment} />}
        QuestionListComponent={
          <VSSQuestionList
            scrollToQuestionByIdFunc={scrollToQuestionByIdFunc}
            onQuestionClick={({question, questionSet}) => {
              return () => {
                setActiveQuestionState(question, questionSet);
              };
            }}
            activeQuestion={activeQuestion}
            questionSets={questionSets}
            guesses={guessesMap}
            paginatedResultsManager={paginatedResultsManager}
          />
        }
        questionRendererProps={{
          actions: getFooterActions(),
          interactive: false,
          rendererType: 'PostGuess',
          navigateBackProps: makeNavigateBackProps({
            paginatedQuestionsListManager: paginatedResultsManager,
            activeQuestionSet,
            activeQuestion
          }),
          navigateNextProps: makeNavigateNextProps({
            paginatedQuestionsListManager: paginatedResultsManager,
            activeQuestionSet,
            activeQuestion
          }),
          headingActions: getHeadingActions()
        }}
      />
    </MobilePracticeViewProvider>
  );
};

export default ViewStudentSubmission;
